import { Timestamp } from 'firebase/firestore'

/** Special compatibility tokens selectable alongside specific vehicle values. */
export const PART_COMPATIBILITY_SPECIAL_VALUES = ['Universal', 'N/A'] as const
export type PartCompatibilitySpecialValue = (typeof PART_COMPATIBILITY_SPECIAL_VALUES)[number]

/** Optional vehicle-fit criteria for a catalog part. Each field accepts specific values and/or Universal / N/A. */
export interface PartCompatibility {
  body?: string[]
  engine?: string[]
  make?: string[]
  model?: string[]
  year?: string[]
  fuel?: string[]
  size?: string[]
}

export interface PartPhoto {
  url: string
  storagePath: string
  fileName: string
  uploadedAt: string
}

export interface Part {
  part_number: string
  sku: string
  manufacturer: string
  name: string
  /** Stable key referencing `repairCategories` (RepairCategoryDoc.key). */
  categoryKey: string
  /** Label from `expenseSubcategories`, scoped to categoryKey. */
  subcategory: string
  description?: string
  /** URL where the part can be purchased. */
  link?: string
  price?: number
  quantity: number
  /** Vehicle fit criteria (body manufacturer, engine, make, model, year, fuel, size). */
  compatibility?: PartCompatibility
  /** Catalog photos stored in Firebase Storage under `parts/{partId}/photos/`. */
  photos?: PartPhoto[]
  created_at: Timestamp
  updated_at: Timestamp
}

export const PARTS_COLLECTION = 'parts'

export type PartRequestStatus = 'requested' | 'ordered' | 'fulfilled' | 'cancelled'

export const PART_REQUEST_STATUSES: PartRequestStatus[] = [
  'requested',
  'ordered',
  'fulfilled',
  'cancelled',
]

/**
 * A request for a part placed by a repair-shop user. Stored in the
 * `part_requests` Firestore collection and surfaced to admins in Parts
 * Management plus an admin "Part Requested" notification.
 */
export interface PartRequest {
  id?: string
  /** Id of the catalog `parts` document being requested. */
  part_id: string
  /** Snapshot of catalog fields at request time (catalog may change later). */
  sku: string
  part_number: string
  name: string
  /** Snapshot of repair category key at request time. */
  categoryKey: string
  /** Snapshot of repair subcategory label at request time. */
  subcategory: string
  quantity: number
  status: PartRequestStatus
  notes?: string
  /** Requesting repair shop context. */
  repair_shop_id?: string
  repair_shop_name?: string
  requested_by_user_id: string
  requested_by_name?: string
  created_at: Timestamp
  updated_at: Timestamp
}

export const PART_REQUESTS_COLLECTION = 'part_requests'

/** Per-user pinned catalog parts. Document id is the Firebase Auth uid. */
export interface PartFavorite {
  user_id: string
  part_ids: string[]
  updated_at: Timestamp
}

export const PART_FAVORITES_COLLECTION = 'part_favorites'

/** Phase-1 step-van chassis. Other models are not advertised as supported. */
export const STEP_VAN_MODELS = ['F59', 'MT45', 'MT55'] as const
export type StepVanModel = (typeof STEP_VAN_MODELS)[number]

export const STEP_VAN_COMPATIBILITY = {
  models: Array.from(STEP_VAN_MODELS),
} as const

export const isStepVanModel = (model: string): boolean =>
  (STEP_VAN_MODELS as readonly string[]).includes(model)

/** True when a part is Universal, N/A, unset, or tagged for a step-van model. */
export const partFitsStepVanPhase = (compatibility?: PartCompatibility): boolean => {
  const models = compatibility?.model
  if (!models?.length) return true
  if (models.includes('Universal') || models.includes('N/A')) return true
  return models.some((model) => isStepVanModel(model))
}

export const inferStepVanModelsFromText = (...chunks: Array<string | undefined>): string[] => {
  const found = new Set<string>()
  for (const chunk of chunks) {
    if (!chunk) continue
    const matches = chunk.match(/\b(F-?59|MT-?45|MT-?55)\b/gi) ?? []
    for (const match of matches) {
      const raw = match.replace(/-/g, '').toUpperCase()
      if (raw === 'F59' || raw === 'MT45' || raw === 'MT55') found.add(raw)
    }
  }
  return Array.from(found)
}

export const PARTS_SCRAPE_RUNS_COLLECTION = 'parts_scrape_runs'

export type PartsScrapeRunStatus = 'queued' | 'running' | 'ok' | 'error'
export type PartsScrapeRunTrigger = 'schedule' | 'manual'

export interface PartsScrapeRun {
  status: PartsScrapeRunStatus
  trigger: PartsScrapeRunTrigger
  requested_by?: string
  ok_count: number
  error_count: number
  skipped_count?: number
  sources?: string[]
  error_message?: string
  started_at?: Timestamp
  finished_at?: Timestamp
  created_at: Timestamp
}

export const PART_TRAINING_LABELS_COLLECTION = 'part_training_labels'

export const PART_TRAINING_LABEL_TYPES = [
  'mirror',
  'bumper',
  'door',
  'fender',
  'hood',
  'grille',
  'headlight',
  'taillight',
  'wheel',
  'windshield',
  'step',
  'other',
] as const

export type PartTrainingLabelType = (typeof PART_TRAINING_LABEL_TYPES)[number]
export type PartTrainingPhotoSource = 'inventory' | 'inspection' | 'damage'

export interface PartTrainingLabelBox {
  x: number
  y: number
  width: number
  height: number
}

export interface PartTrainingLabel {
  vehicle_id: string
  vehicle_label?: string
  photo_url: string
  photo_path?: string
  photo_source: PartTrainingPhotoSource
  label: PartTrainingLabelType
  box?: PartTrainingLabelBox
  labeled_by: string
  labeled_by_name?: string
  created_at: Timestamp
  updated_at: Timestamp
}
